import { Alert, Button, Checkbox, Form, Modal } from "antd";
import React, { useEffect, useRef, useState } from "react";

const SCROLL_END_THRESHOLD = 24;

interface ConsentCheckboxFieldProps {
  name: "terms_agreed" | "privacy_agreed";
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  linkLabel: string;
  requiredMessage: string;
  modalTitle: string;
  modalContent?: React.ReactNode;
  style?: React.CSSProperties;
}

const ConsentCheckboxField = ({
  name,
  checked,
  onCheckedChange,
  linkLabel,
  requiredMessage,
  modalTitle,
  modalContent,
  style,
}: ConsentCheckboxFieldProps) => {
  const [open, setOpen] = useState(false);
  // Sticky once true: sets once the user has genuinely scrolled the content into view.
  const [manuallyReachedEnd, setManuallyReachedEnd] = useState(false);
  // Live, not sticky: true whenever the content doesn't overflow (nothing to scroll to), e.g.
  // a short PDF, or the loading placeholder before pages have rendered in.
  const [hasOverflow, setHasOverflow] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrolledToEnd = manuallyReachedEnd || !hasOverflow;

  const openModal = () => {
    setManuallyReachedEnd(false);
    setHasOverflow(true);
    setOpen(true);
  };

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD) {
      setManuallyReachedEnd(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    const el = scrollContainerRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasOverflow(el.scrollHeight > el.clientHeight + SCROLL_END_THRESHOLD);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [open]);

  const onAgree = () => {
    onCheckedChange(true);
    setOpen(false);
  };

  const onDecline = () => {
    onCheckedChange(false);
    setOpen(false);
  };

  return (
    <>
      <Form.Item
        label=""
        name={name}
        validateTrigger={["onSubmit"]}
        style={style}
        rules={[
          {
            validator: (_, value) => {
              if (value === true || checked === true) return Promise.resolve();
              return Promise.reject(new Error(requiredMessage));
            },
          },
        ]}
      >
        <Checkbox
          checked={checked}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openModal();
          }}
        >
          By proceeding, I acknowledge that I have read and agreed to this{" "}
          <span style={{ color: "#dc1a22", textDecoration: "underline", fontWeight: 500 }}>{linkLabel}</span>
        </Checkbox>
      </Form.Item>

      <Modal
        title={modalTitle}
        open={open}
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="decline" onClick={onDecline}>Decline</Button>,
          <Button key="agree" type="primary" disabled={!scrolledToEnd} onClick={onAgree}>Agree</Button>,
        ]}
        centered
        width={1000}
      >
        {!scrolledToEnd && (
          <Alert
            type="info"
            showIcon
            message='Please scroll to the end of the PDF to enable the "Agree" button.'
            style={{ marginBottom: 16 }}
          />
        )}
        <div
          ref={scrollContainerRef}
          onScroll={onScroll}
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          {modalContent}
        </div>
      </Modal>
    </>
  );
};

export default ConsentCheckboxField;
